import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { 
  ArrowRight, Zap, FileText, Bell, BarChart3, 
  Sparkles, Shield, Download, Lock, Building
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';

const SolutionPME = () => {
  const features = [
    {
      icon: Sparkles,
      title: "Scanner IA de factures",
      description: "Photographiez vos factures fournisseurs, l'IA extrait automatiquement toutes les données."
    },
    {
      icon: Bell,
      title: "Relances intelligentes",
      description: "Relances automatiques personnalisables par client. Scénarios prédéfinis ou sur mesure."
    },
    {
      icon: BarChart3,
      title: "Dashboard complet",
      description: "Visualisez votre CA, vos factures en attente et votre trésorerie en temps réel."
    },
    {
      icon: Download,
      title: "Export comptable CSV",
      description: "Exportez vos données pour votre expert-comptable en 1 clic."
    },
    {
      icon: Shield,
      title: "Format Factur-X",
      description: "Factures conformes à la réforme 2026, prêtes pour l'envoi PPF/PDP."
    },
    {
      icon: Lock,
      title: "Sécurité entreprise",
      description: "Données chiffrées, hébergement français, conforme RGPD."
    }
  ];

  const stats = [
    { value: "75%", label: "de temps gagné sur la facturation" },
    { value: "30%", label: "d'impayés en moins" },
    { value: "10 ans", label: "d'archivage légal inclus" }
  ];

  const plans = [
    {
      name: "Pro",
      price: "29€",
      features: ["Factures illimitées", "3 utilisateurs", "Relances auto", "Export comptable"],
      highlight: false
    },
    {
      name: "Expert",
      price: "49€",
      features: ["Tout Pro", "Utilisateurs illimités", "Scanner IA", "Rapprochement bancaire", "Support prioritaire"],
      highlight: true
    }
  ];

  const faqs = [
    {
      q: "SmartFacture AI est-il adapté aux PME ?",
      a: "Oui, SmartFacture AI propose des fonctionnalités avancées : scanner IA, relances automatiques, dashboard temps réel, et export CSV pour votre comptable."
    },
    {
      q: "Comment fonctionne le scanner IA ?",
      a: "Photographiez ou uploadez vos factures fournisseurs. L'IA extrait automatiquement toutes les données : montants, dates, fournisseur, TVA."
    },
    {
      q: "L'export comptable est-il compatible avec mon logiciel ?",
      a: "Oui, l'export CSV est compatible avec tous les logiciels comptables. Transmettez facilement vos données à votre expert-comptable."
    },
    {
      q: "Les données sont-elles sécurisées ?",
      a: "Oui, vos données sont chiffrées et hébergées en France. Nous sommes conformes RGPD."
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": { "@type": "Answer", "text": faq.a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Logiciel Facturation PME | SmartFacture AI - Scanner IA & Relances Auto</title>
        <meta name="description" content="Logiciel de facturation pour PME et TPE. Scanner IA, relances automatiques, export comptable. Conforme 2026. Essai gratuit." />
        <link rel="canonical" href="https://smartfacture-ai.com/solutions/pme" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-xl flex items-center justify-center">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">SmartFacture AI</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/pricing" className="text-slate-600 hover:text-slate-900 hidden sm:block">
                Tarifs
              </Link>
              <Link to="/register">
                <Button className="bg-blue-600 hover:bg-blue-700">Essai gratuit</Button>
              </Link>
            </div>
          </div>
        </header>

        {/* Hero */}
        <section className="bg-gradient-to-br from-blue-600 to-indigo-700 text-white py-16 lg:py-24">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center max-w-3xl mx-auto">
              <Badge className="bg-white/20 text-white border-white/30 mb-6">
                Solution PME & TPE
              </Badge>
              <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                La facturation intelligente pour votre PME
              </h1>
              <p className="text-xl text-blue-100 mb-8">
                Scanner IA, relances automatiques, dashboard temps réel. Tout ce qu'il faut pour digitaliser votre facturation et gagner du temps.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <Button size="lg" className="w-full sm:w-auto bg-white text-blue-600 hover:bg-blue-50">
                    Essai gratuit 14 jours
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/demo">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-white text-white hover:bg-white/10">
                    Demander une démo
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-8 bg-white border-b">
          <div className="max-w-6xl mx-auto px-4">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              {stats.map((s, i) => (
                <div key={i}>
                  <p className="text-3xl font-bold text-blue-600">{s.value}</p>
                  <p className="text-slate-600">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-4">
              Fonctionnalités avancées pour PME
            </h2>
            <p className="text-slate-600 text-center mb-12 max-w-2xl mx-auto">
              Des outils puissants pour digitaliser et automatiser la gestion financière de votre entreprise.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((f, i) => {
                const Icon = f.icon;
                return (
                  <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                      <Icon className="h-6 w-6 text-blue-600" />
                    </div>
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">{f.title}</h3>
                    <p className="text-slate-600">{f.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Plans */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Tarifs pour PME
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              {plans.map((p, i) => (
                <div key={i} className={`bg-white rounded-2xl p-8 ${p.highlight ? 'ring-2 ring-blue-500 shadow-lg' : 'border border-slate-200'}`}>
                  {p.highlight && (
                    <Badge className="bg-blue-500 text-white mb-4">Le plus populaire</Badge>
                  )}
                  <h3 className="text-2xl font-bold text-slate-900">{p.name}</h3>
                  <div className="mt-4 mb-6">
                    <span className="text-4xl font-bold text-slate-900">{p.price}</span>
                    <span className="text-slate-500">/mois</span>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {p.features.map((f, j) => (
                      <li key={j} className="flex items-center gap-2">
                        <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center">
                          <svg className="w-3 h-3 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <span className="text-slate-700">{f}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to="/register">
                    <Button className={`w-full ${p.highlight ? 'bg-blue-600 hover:bg-blue-700' : 'bg-slate-900 hover:bg-slate-800'}`}>
                      Commencer l'essai gratuit
                    </Button>
                  </Link>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust */}
        <section className="py-16">
          <div className="max-w-6xl mx-auto px-4">
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 lg:p-12 text-white">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-2xl lg:text-3xl font-bold mb-4">
                    Sécurité et conformité garanties
                  </h2>
                  <ul className="space-y-3">
                    {[
                      "Données chiffrées AES-256",
                      "Hébergement en France",
                      "Conforme RGPD",
                      "Certifié ISO 27001",
                      "Audit trail complet"
                    ].map((item, i) => (
                      <li key={i} className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-blue-300" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-center">
                  <Building className="h-24 w-24 mx-auto mb-4 text-blue-300" />
                  <p className="text-blue-100">
                    Solution de facturation pour PME françaises
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-slate-100">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-3xl font-bold text-slate-900 text-center mb-12">
              Questions fréquentes
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {faqs.map((faq, i) => (
                <div key={i} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-slate-900 mb-2">{faq.q}</h3>
                  <p className="text-slate-600 text-sm">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Prêt à digitaliser votre facturation ?
            </h2>
            <p className="text-slate-600 mb-8">
              Essai gratuit 14 jours. Sans engagement. Sans carte bancaire.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register">
                <Button size="lg" className="bg-blue-600 hover:bg-blue-700">
                  Démarrer l'essai gratuit
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="lg" variant="outline">
                  Contacter un conseiller
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-slate-400">© 2026 SmartFacture AI. Tous droits réservés.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default SolutionPME;
